import { useState } from 'react';
import styled from 'styled-components';

import { useMutation, useQueryClient } from '@tanstack/react-query';

import ActivityInput from 'components/shared/activityInput/ActivityInput';
import useActivityInput from 'components/shared/activityInput/useActivityInput';
import Button from 'components/shared/Button';
import { Select } from 'components/shared/form/InfiniteScrollDropdown';
import InputGroup from 'components/shared/form/InputGroup';
import StandardModal, { Actions, PatientName } from 'components/shared/StandardModal';
import { HOME_HEALTH_AGENCY } from 'constants/locationTypes';
import Group from 'models/Group';
import LocationEpisode from 'models/LocationEpisode';
import { ReviewStatus } from 'models/Review';
import ServiceRefusal from 'models/reviews/ServiceRefusal';
import { episodeQueryKeys } from 'services/api/episode';
import { useInfiniteGroups } from 'services/api/group';
import { updateReview } from 'services/api/reviews';

export type ReassignServiceRefusalType = {
  setShow: (b: boolean) => void;
  patientName: string;
  locationEpisode: LocationEpisode;
  serviceRefusal: ServiceRefusal;
};

export default function ReassignServiceRefusal(props: ReassignServiceRefusalType) {
  const { setShow, patientName, locationEpisode, serviceRefusal } = props;
  const queryClient = useQueryClient();
  const [reassignmentGroup, setReassignmentGroup] = useState<Group | null>(null);
  const { note, setNote, resetNote, getSerializedNoteValues } = useActivityInput();
  const [isUploading, setIsUploading] = useState(false);

  const { mutate, isPending } = useMutation({
    mutationFn: updateReview,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: episodeQueryKeys.show({ id: locationEpisode.episodeId }),
      });
      setShow(false);
    },
  });

  const asyncGroupOptions = useInfiniteGroups({
    sortBy: 'name asc',
    networkClientId: locationEpisode.owner.clientId,
    type: HOME_HEALTH_AGENCY,
    viewOnly: true,
  });

  const cancel = () => {
    setShow(false);
    setReassignmentGroup(null);
    resetNote();
  };

  const submit = () => {
    if (!reassignmentGroup) return;

    const note = getSerializedNoteValues();
    delete note.escalationType;

    mutate({
      id: serviceRefusal.id,
      type: serviceRefusal.type,
      note,
      reassignedTo: {
        id: reassignmentGroup?.id,
        name: reassignmentGroup?.name,
      },
      status: ReviewStatus.REJECTED, // TODO: some conditional logic in main app setting to canceled if reassigning to same group
      include: 'activities.attachments,episode',
    });
  };

  return (
    <StandardModal title='Reassign Services' onCancel={cancel}>
      <PatientName>Patient: {patientName}</PatientName>
      <FormContainer>
        <InputGroup title={'Where will the patient be receiving services?'}>
          <Select<Group>
            {...asyncGroupOptions}
            onChange={(change) => setReassignmentGroup(change)}
            getOptionValue={(prop) => prop.id}
            getOptionLabel={(prop) => prop.name}
            value={reassignmentGroup}
            populateDefault
          />
        </InputGroup>
        <InputGroup title={'Notes (optional)'}>
          <ActivityInput
            values={note}
            setValues={setNote}
            locationEpisodeId={locationEpisode.id}
            onUploading={setIsUploading}
          />
        </InputGroup>
      </FormContainer>
      <Actions>
        <Button variant='ghost' onClick={cancel}>
          Cancel
        </Button>
        <Button onClick={submit} disabled={!reassignmentGroup || isUploading} loading={isPending}>
          Confirm
        </Button>
      </Actions>
    </StandardModal>
  );
}

const FormContainer = styled.div`
  margin-bottom: 36px;
`;
